'use client';

import { useMemo, useState, type FC } from 'react';
import type { ToolCallMessagePartComponent } from '@assistant-ui/react';
import { FileTextIcon, Loader2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchMcpPdfFile } from '../api/mcpPdfApi';

export type RenderSeoReportPdfArgs = {
  analyze_markdown?: string;
  title?: string;
} & Record<string, unknown>;

type PdfToolResult = {
  ok?: boolean;
  rendered?: boolean;
  mimeType?: string;
  filename?: string;
  title?: string;
  byteLength?: number;
  pdfBase64?: string;
  downloadPath?: string;
  fileId?: string;
  pdf_stored?: boolean;
};

function parsePdfResult(result: unknown): PdfToolResult | null {
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return result as PdfToolResult;
  }
  if (typeof result !== 'string' || !result.trim()) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(result);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as PdfToolResult;
    }
  } catch {
    return null;
  }
  return null;
}

function formatBytes(bytes: number | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function sanitizeFileName(name: string): string {
  const cleaned = name
    .trim()
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 80);
  if (!cleaned) return 'seo-report.pdf';
  return cleaned.toLowerCase().endsWith('.pdf') ? cleaned : `${cleaned}.pdf`;
}

const PdfLoading: FC = () => (
  <div
    className="border-border bg-[var(--card)] text-muted-foreground my-2 flex items-center gap-2 rounded-xl border px-4 py-3 text-sm"
    dir="rtl"
  >
    <Loader2Icon className="size-4 shrink-0 animate-spin" />
    در حال ساخت PDF گزارش…
  </div>
);

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function blobFromBase64(pdfBase64: string, mimeType: string | undefined): Blob {
  const binary = atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType || 'application/pdf' });
}

export const PdfReportToolUI: ToolCallMessagePartComponent<
  RenderSeoReportPdfArgs,
  unknown
> = function PdfReportToolUI({ args, result, status, isError }) {
  const parsed = useMemo(() => parsePdfResult(result), [result]);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const canDownload = Boolean(parsed?.downloadPath || parsed?.pdfBase64);

  if (status.type === 'running' && !canDownload) {
    return <PdfLoading />;
  }

  if (isError) {
    return (
      <div
        className="border-destructive/40 bg-destructive/10 text-destructive my-2 rounded-xl border px-4 py-3 text-sm"
        dir="rtl"
      >
        ساخت PDF ناموفق بود.
      </div>
    );
  }

  if (!parsed || (!canDownload && !parsed.rendered && parsed.pdf_stored !== false)) {
    return null;
  }

  const title =
    (typeof parsed.title === 'string' && parsed.title) ||
    (typeof args?.title === 'string' && args.title) ||
    'گزارش SEO';
  const filename = sanitizeFileName(
    (typeof parsed.filename === 'string' && parsed.filename) || title
  );
  const sizeLabel = formatBytes(parsed.byteLength);

  const downloadPdf = async () => {
    if (!canDownload || downloading) return;
    setDownloadError(false);
    setDownloading(true);
    try {
      const blob = parsed.downloadPath
        ? await fetchMcpPdfFile(parsed.downloadPath)
        : blobFromBase64(parsed.pdfBase64!, parsed.mimeType);
      saveBlob(blob, filename);
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className={cn(
        'border-border bg-[var(--card)] my-2 overflow-hidden rounded-xl border px-4 py-3'
      )}
      dir="rtl"
    >
      <div className="flex items-center gap-3">
        <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-lg">
          <FileTextIcon className="size-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-foreground truncate text-sm font-semibold">
            {title}
          </h3>
          <p className="text-muted-foreground mt-0.5 text-xs">
            PDF گزارش SEO
            {sizeLabel ? ` · ${sizeLabel}` : ''}
          </p>
        </div>
        {canDownload ? (
          <button
            type="button"
            onClick={() => {
              void downloadPdf();
            }}
            disabled={downloading}
            className="bg-primary text-primary-foreground hover:bg-primary/90 shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-60"
          >
            {downloading ? 'در حال دانلود…' : 'دانلود PDF'}
          </button>
        ) : null}
      </div>
      {!canDownload ? (
        <p className="text-muted-foreground mt-3 text-xs">
          فایل PDF برای دانلود در دسترس نیست.
        </p>
      ) : null}
      {downloadError ? (
        <p className="text-destructive mt-2 text-xs">
          دانلود PDF ناموفق بود. دوباره تلاش کنید.
        </p>
      ) : null}
    </div>
  );
};
